import React from "react";
import { useNavigate } from "react-router-dom";
import { useAnalyses } from "@/context/AnalysisContext";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/EmptyState";
import { AdAnalysisDetail } from "@/components/AdAnalysisDetail";
import { HistoryCard } from "@/components/HistoryCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Film, Gauge, Trophy } from "lucide-react";

function MiniStat({ icon: Icon, label, value }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="h-4 w-4" />
        <span className="overline">{label}</span>
      </div>
      <div className="font-display text-3xl font-bold tabular tracking-tight">{value}</div>
    </Card>
  );
}

export default function Overview() {
  const { analyses } = useAnalyses();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (analyses.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <p className="overline text-muted-foreground">Welcome back, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Overview</h1>
        </div>
        <EmptyState />
      </div>
    );
  }

  const latest = analyses[0];
  const avg = Math.round(analyses.reduce((s, a) => s + (a.overall_score || 0), 0) / analyses.length);
  const best = Math.max(...analyses.map((a) => a.overall_score || 0));

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-muted-foreground">Welcome back, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Overview</h1>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <MiniStat icon={Film} label="Analyses" value={analyses.length} />
        <MiniStat icon={Gauge} label="Avg Score" value={avg} />
        <MiniStat icon={Trophy} label="Top Score" value={best} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold tracking-tight">Latest analysis</h2>
        </div>
        <AdAnalysisDetail analysis={latest} />
      </div>

      {analyses.length > 1 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold tracking-tight">Recent teardowns</h2>
            <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate("/library")}>
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {analyses.slice(1, 7).map((a) => (
              <HistoryCard key={a.id} analysis={a} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
